import React, { useState, useEffect } from 'react';
import { useProject } from '@/contexts/ProjectContext';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  Zap, 
  Plus, 
  Edit, 
  Trash2, 
  Play, 
  Pause, 
  Settings,
  ArrowRight,
  Clock,
  Bell,
  Mail,
  MessageSquare,
  CheckCircle,
  AlertTriangle,
  Filter,
  Save
} from 'lucide-react';
import { TaskStatus, TaskPriority } from '@/types';
import { toast } from 'sonner';

interface WorkflowRule {
  id: string;
  name: string;
  description: string;
  isActive: boolean;
  trigger: {
    type: 'status_change' | 'time_based' | 'assignee_change' | 'priority_change' | 'due_date';
    conditions: {
      fromStatus?: TaskStatus;
      toStatus?: TaskStatus;
      priority?: TaskPriority;
      timeValue?: number;
      timeUnit?: 'hours' | 'days';
      assigneeId?: string;
    };
  };
  actions: {
    type: 'change_status' | 'send_notification' | 'assign_user' | 'add_comment' | 'send_email';
    parameters: {
      newStatus?: TaskStatus;
      message?: string;
      assigneeId?: string;
      notifyUsers?: string[];
      emailTemplate?: string;
    };
  }[];
  createdAt: Date;
  lastTriggered?: Date;
  executionCount: number;
}

export const WorkflowAutomation: React.FC = () => {
  const { projects, tasks, currentProject } = useProject();
  const { user } = useAuth();
  const [rules, setRules] = useState<WorkflowRule[]>([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState<WorkflowRule | null>(null);
  const [filter, setFilter] = useState<'all' | 'active' | 'inactive'>('all');

  // Mock workflow rules
  useEffect(() => {
    const mockRules: WorkflowRule[] = [
      {
        id: '1',
        name: 'Auto-aprovação para tarefas de baixa prioridade',
        description: 'Quando uma tarefa de baixa prioridade é marcada como "Em Revisão", automaticamente mover para "Concluído"',
        isActive: true,
        trigger: {
          type: 'status_change',
          conditions: {
            fromStatus: TaskStatus.IN_PROGRESS,
            toStatus: TaskStatus.REVIEW,
            priority: TaskPriority.LOW
          }
        },
        actions: [
          {
            type: 'change_status',
            parameters: {
              newStatus: TaskStatus.DONE
            }
          },
          {
            type: 'send_notification',
            parameters: {
              message: 'Tarefa de baixa prioridade aprovada automaticamente',
              notifyUsers: ['assignee', 'creator']
            }
          }
        ],
        createdAt: new Date('2024-01-15'),
        lastTriggered: new Date('2024-01-16'),
        executionCount: 23
      },
      {
        id: '2',
        name: 'Notificação de prazo próximo',
        description: 'Enviar notificação 24 horas antes do prazo de entrega',
        isActive: true,
        trigger: {
          type: 'due_date',
          conditions: {
            timeValue: 24,
            timeUnit: 'hours'
          }
        },
        actions: [
          {
            type: 'send_notification',
            parameters: {
              message: 'Atenção: Prazo de entrega em 24 horas!',
              notifyUsers: ['assignee', 'project_manager']
            }
          },
          {
            type: 'send_email',
            parameters: {
              emailTemplate: 'due_date_reminder',
              message: 'Lembrete: Sua tarefa vence em 24 horas'
            }
          }
        ],
        createdAt: new Date('2024-01-10'),
        lastTriggered: new Date('2024-01-17'),
        executionCount: 45
      },
      {
        id: '3',
        name: 'Escalação para gerente',
        description: 'Quando uma tarefa urgente fica mais de 2 dias sem atualização, notificar o gerente',
        isActive: true,
        trigger: {
          type: 'time_based',
          conditions: {
            timeValue: 2,
            timeUnit: 'days',
            priority: TaskPriority.URGENT
          }
        },
        actions: [
          {
            type: 'send_notification',
            parameters: {
              message: 'Tarefa urgente sem atualização há 2 dias',
              notifyUsers: ['project_manager', 'admin']
            }
          },
          {
            type: 'add_comment',
            parameters: {
              message: 'Tarefa escalada automaticamente devido à falta de atualização'
            }
          }
        ],
        createdAt: new Date('2024-01-08'),
        lastTriggered: new Date('2024-01-15'),
        executionCount: 7
      },
      {
        id: '4',
        name: 'Reatribuição automática',
        description: 'Quando uma tarefa é rejeitada na revisão, reatribuir para o desenvolvedor original',
        isActive: false,
        trigger: {
          type: 'status_change',
          conditions: {
            fromStatus: TaskStatus.REVIEW,
            toStatus: TaskStatus.IN_PROGRESS
          }
        },
        actions: [
          {
            type: 'assign_user',
            parameters: {
              assigneeId: 'original_assignee'
            }
          },
          {
            type: 'send_notification',
            parameters: {
              message: 'Tarefa reatribuída após revisão',
              notifyUsers: ['assignee']
            }
          }
        ],
        createdAt: new Date('2024-01-12'),
        executionCount: 12
      }
    ];

    setRules(mockRules);
  }, []);

  const [newRule, setNewRule] = useState<Partial<WorkflowRule>>({
    name: '',
    description: '',
    isActive: true,
    trigger: {
      type: 'status_change',
      conditions: {}
    },
    actions: []
  });

  const filteredRules = rules.filter(rule => {
    if (filter === 'active') return rule.isActive;
    if (filter === 'inactive') return !rule.isActive;
    return true;
  });

  const handleToggleRule = (ruleId: string) => {
    setRules(prev => prev.map(rule => 
      rule.id === ruleId ? { ...rule, isActive: !rule.isActive } : rule
    ));
    
    const rule = rules.find(r => r.id === ruleId);
    toast.success(`Regra "${rule?.name}" ${rule?.isActive ? 'desativada' : 'ativada'}`);
  };

  const handleDeleteRule = (ruleId: string) => {
    setRules(prev => prev.filter(rule => rule.id !== ruleId));
    toast.success('Regra removida com sucesso');
  };

  const handleCreateRule = () => {
    if (!newRule.name || !newRule.description) {
      toast.error('Nome e descrição são obrigatórios');
      return;
    }

    const rule: WorkflowRule = {
      id: Date.now().toString(),
      name: newRule.name,
      description: newRule.description,
      isActive: newRule.isActive || true,
      trigger: newRule.trigger || { type: 'status_change', conditions: {} },
      actions: newRule.actions || [],
      createdAt: new Date(),
      executionCount: 0
    };

    setRules(prev => [...prev, rule]);
    setIsCreateDialogOpen(false);
    setNewRule({ name: '', description: '', isActive: true, trigger: { type: 'status_change', conditions: {} }, actions: [] });
    toast.success('Regra criada com sucesso');
  };

  const getTriggerIcon = (type: string) => {
    switch (type) {
      case 'status_change': return <ArrowRight className="w-4 h-4" />;
      case 'time_based': return <Clock className="w-4 h-4" />;
      case 'due_date': return <Bell className="w-4 h-4" />;
      case 'assignee_change': return <Settings className="w-4 h-4" />;
      default: return <Zap className="w-4 h-4" />;
    }
  };

  const getTriggerText = (type: string) => {
    switch (type) {
      case 'status_change': return 'Mudança de Status';
      case 'time_based': return 'Baseado em Tempo';
      case 'due_date': return 'Prazo de Entrega';
      case 'assignee_change': return 'Mudança de Responsável';
      case 'priority_change': return 'Mudança de Prioridade';
      default: return type;
    }
  };

  const getActionIcon = (type: string) => {
    switch (type) {
      case 'change_status': return <ArrowRight className="w-4 h-4" />;
      case 'send_notification': return <Bell className="w-4 h-4" />;
      case 'assign_user': return <Settings className="w-4 h-4" />;
      case 'add_comment': return <MessageSquare className="w-4 h-4" />;
      case 'send_email': return <Mail className="w-4 h-4" />;
      default: return <Zap className="w-4 h-4" />;
    }
  };

  const getActionText = (type: string) => {
    switch (type) {
      case 'change_status': return 'Alterar Status';
      case 'send_notification': return 'Enviar Notificação';
      case 'assign_user': return 'Atribuir Usuário';
      case 'add_comment': return 'Adicionar Comentário';
      case 'send_email': return 'Enviar Email';
      default: return type;
    }
  };

  const mockUsers = [
    { id: '1', name: 'Admin User' },
    { id: '2', name: 'Manager User' },
    { id: '3', name: 'Collaborator User' }
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Automação de Workflows</h1>
          <p className="text-gray-600 mt-1">Configure regras automáticas para otimizar seu fluxo de trabalho</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="outline">
            <Settings className="w-4 h-4 mr-2" />
            Configurações
          </Button>
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Nova Regra
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Regras Ativas</p>
                <p className="text-2xl font-bold text-green-600">{rules.filter(r => r.isActive).length}</p>
              </div>
              <Play className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Execuções Hoje</p>
                <p className="text-2xl font-bold text-blue-600">{rules.reduce((sum, rule) => sum + rule.executionCount, 0)}</p>
              </div>
              <Zap className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Tempo Economizado</p>
                <p className="text-2xl font-bold text-purple-600">2.5h</p>
              </div>
              <Clock className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Taxa de Sucesso</p>
                <p className="text-2xl font-bold text-green-600">98%</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center space-x-4">
        <div className="flex items-center space-x-2">
          <Filter className="w-4 h-4 text-gray-400" />
          <span className="text-sm text-gray-600">Filtrar por:</span>
        </div>
        <Select value={filter} onValueChange={(value) => setFilter(value as any)}>
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            <SelectItem value="active">Ativas</SelectItem>
            <SelectItem value="inactive">Inativas</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Rules List */}
      <div className="space-y-4">
        {filteredRules.map(rule => (
          <Card key={rule.id} className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Switch
                    checked={rule.isActive}
                    onCheckedChange={() => handleToggleRule(rule.id)}
                  />
                  <div>
                    <CardTitle className="text-lg">{rule.name}</CardTitle>
                    <p className="text-sm text-gray-600">{rule.description}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Badge variant={rule.isActive ? 'default' : 'secondary'}>
                    {rule.isActive ? 'Ativa' : 'Inativa'}
                  </Badge>
                  <Button variant="ghost" size="sm">
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDeleteRule(rule.id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Trigger */}
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2 text-sm">
                    {getTriggerIcon(rule.trigger.type)}
                    <span className="font-medium text-gray-700">Gatilho:</span>
                    <span className="text-gray-600">{getTriggerText(rule.trigger.type)}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="space-y-2">
                  <div className="flex items-center space-x-2 text-sm">
                    <ArrowRight className="w-4 h-4 text-gray-400" />
                    <span className="font-medium text-gray-700">Ações:</span>
                  </div>
                  <div className="pl-6 space-y-1">
                    {rule.actions.map((action, index) => (
                      <div key={index} className="flex items-center space-x-2 text-sm">
                        {getActionIcon(action.type)}
                        <span className="text-gray-600">{getActionText(action.type)}</span>
                        {action.parameters.message && (
                          <span className="text-gray-500">- {action.parameters.message}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Stats */}
                <div className="flex items-center justify-between text-sm text-gray-500 pt-2 border-t">
                  <div className="flex items-center space-x-4">
                    <span>Executada {rule.executionCount} vezes</span>
                    {rule.lastTriggered && (
                      <span>Última execução: {rule.lastTriggered.toLocaleDateString('pt-BR')}</span>
                    )}
                  </div>
                  <span>Criada em {rule.createdAt.toLocaleDateString('pt-BR')}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create Rule Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Nova Regra de Automação</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="ruleName">Nome da Regra</Label>
                <Input
                  id="ruleName"
                  value={newRule.name || ''}
                  onChange={(e) => setNewRule(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ex: Notificação de prazo próximo"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="ruleDescription">Descrição</Label>
                <Textarea
                  id="ruleDescription"
                  value={newRule.description || ''}
                  onChange={(e) => setNewRule(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Descreva quando e como esta regra será executada..."
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label>Gatilho</Label>
                <Select 
                  value={newRule.trigger?.type || 'status_change'} 
                  onValueChange={(value) => setNewRule(prev => ({ 
                    ...prev, 
                    trigger: { ...prev.trigger, type: value as any, conditions: {} } 
                  }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="status_change">Mudança de Status</SelectItem>
                    <SelectItem value="time_based">Baseado em Tempo</SelectItem>
                    <SelectItem value="due_date">Prazo de Entrega</SelectItem>
                    <SelectItem value="assignee_change">Mudança de Responsável</SelectItem>
                    <SelectItem value="priority_change">Mudança de Prioridade</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  checked={newRule.isActive || true}
                  onCheckedChange={(checked) => setNewRule(prev => ({ ...prev, isActive: checked }))}
                />
                <Label>Ativar regra imediatamente</Label>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">Exemplo de Configuração</h4>
              <p className="text-sm text-blue-800">
                Quando uma tarefa muda de "Em Andamento" para "Em Revisão", 
                enviar notificação para o gerente do projeto e adicionar comentário automático.
              </p>
            </div>

            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleCreateRule}>
                <Save className="w-4 h-4 mr-2" />
                Criar Regra
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};